import { fetchAuth } from "../../../shared/api/ApiService"
import { MyError } from "../../../shared/lib/error/MyError"
import { IComment, ILesson, INotification, IStudent, IStudentByPaymentUrl, IStudentChange, IStudentData,
    IStudentCreateData, IStudentFinance, IStudentTag, IStudentTagResponse, IStudentUpdateData, IWorkTgOption,
    IWorkTgOptionResponse, IPaymentConsent, IPaymentLegalInfo, ITransaction } from "../model/types"
import { IBusinessLabel } from '../../../shared/model/labels'

const normalizeLabels = (value: unknown): IBusinessLabel[] => Array.isArray(value)
    ? value.map(item => {
        const label = item as Record<string, unknown>
        return {
            text: String(label.text ?? label.Text ?? ''),
            background_color: String(label.background_color ?? label.backgroundColor ?? label.BackgroundColor ?? ''),
            text_color: String(label.text_color ?? label.textColor ?? label.TextColor ?? ''),
        }
    }).filter(label => Boolean(label.text))
    : []

const normalizeStudentWorkTgs = (value: unknown): {ids: number[]; names: string[]} | null => {
    if(!Array.isArray(value)){
        return null
    }

    return value.reduce<{ids: number[]; names: string[]}>((result, item) => {
        if(typeof item === 'number' || typeof item === 'string'){
            const id = Number(item)
            if(Number.isFinite(id) && id > 0){
                result.ids.push(id)
            }
            return result
        }

        if(item && typeof item === 'object'){
            const workTg = item as Record<string, unknown>
            const id = Number(workTg.id)
            if(Number.isFinite(id) && id > 0){
                result.ids.push(id)
            }

            const name = typeof workTg.name === 'string' ? workTg.name.trim() : ''
            if(name){
                result.names.push(name)
            }
        }

        return result
    }, {ids: [], names: []})
}

const normalizeStudent = <T extends IStudent | IStudentData>(student: T): T => {
    const data = student as unknown as Record<string, unknown>
    const labels = normalizeLabels(data.labels ?? data.Labels ?? data.businessLabels)
    const workTgs = normalizeStudentWorkTgs(data.work_tg_ids)

    return {
        ...student,
        labels,
        ...(workTgs ? {
            work_tg_ids: workTgs.ids,
            work_tg_name: workTgs.names[0] || data.work_tg_name || '',
        } : {}),
    } as T
}

const mapStudentTag = (tag: IStudentTagResponse): IStudentTag => ({
    id: Number(tag.id),
    tag_name: tag.tag_name || tag.tagName || '',
    students_count: Number(tag.students_count ?? tag.studentsCount ?? 0),
    tutors_count: Number(tag.tutors_count ?? tag.tutorsCount ?? 0),
})

const mapWorkTgOption = (workTg: IWorkTgOptionResponse): IWorkTgOption => ({
    id: Number(workTg.id),
    name: workTg.name,
})

class StudentService {

    controller: AbortController | null
    tutorStudentsRequests: Map<number, Promise<{students: IStudent[], students_count: number}>>
    constructor(){
        this.controller = null;
        this.tutorStudentsRequests = new Map()
    }

    async create(student: IStudentChange){
        const data: IStudentCreateData = {
            first_name: student.first_name,
            last_name: student.last_name,
            middle_name: student.middle_name,
            phone: student.phone,
            tg: student.tg,
            cost_per_hour: student.cost_per_hour,
            user_id: student.user_id,
            parent_full_name: student.parent_full_name,
            parent_phone: student.parent_phone,
            parent_tg: student.parent_tg,
        }

        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students', {
            method: "POST",
            body: JSON.stringify(data)
        })
    }

    async payment(
        hash: string,
        amount: string,
        legalConfirmations?: IPaymentConsent,
    ){
        const requestData = {amount, ...legalConfirmations}
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL + '/payment/' + hash, {
            method: "POST",
            body: JSON.stringify(requestData)
        })
        const data: {payment_url?: string} = await res.json();

        if (data.payment_url) {
            window.location.href = data.payment_url;
        } else {
            throw new Error('No payment URL received');
        }
    }

    async getPaymentLegalInfo(hash: string): Promise<IPaymentLegalInfo | null> {
        try{
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL + '/payment/' + hash + '/legal_info', {
                method: "POST"
            })
            const data: IPaymentLegalInfo = await res.json()

            return data
        }
        catch(e){
            if(e instanceof MyError && e.status === 404){
                return null
            }
            throw e
        }
    }

    async update(student: IStudentChange){
        const data: IStudentUpdateData = {
            first_name: student.first_name,
            last_name: student.last_name,
            middle_name: student.middle_name,
            phone: student.phone,
            tg: student.tg,
            cost_per_hour: student.cost_per_hour,
            user_id: student.user_id,
            parent_full_name: student.parent_full_name,
            parent_phone: student.parent_phone,
            parent_tg: student.parent_tg,
        }

        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + student.id, {
            method: "POST",
            body: JSON.stringify(data)
        })
    }

    async getAllByTutor(tutorId: number): Promise<{students: IStudent[], students_count: number}>{
        const currentRequest = this.tutorStudentsRequests.get(tutorId)
        if(currentRequest){
            return currentRequest
        }

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students?tutor_id=${tutorId}`)
            const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
            return {students: students.map(normalizeStudent), students_count}
        })()

        this.tutorStudentsRequests.set(tutorId, request)

        try{
            return await request
        }
        finally{
            this.tutorStudentsRequests.delete(tutorId)
        }
    }

    async getAllByTutorUserId(userId: number): Promise<{students: IStudent[], students_count: number}>{
        const currentRequest = this.tutorStudentsRequests.get(userId)
        if(currentRequest){
            return currentRequest
        }

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students/by_tutor/${userId}`)
            const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
            return {students: students.map(normalizeStudent), students_count}
        })()

        this.tutorStudentsRequests.set(userId, request)

        try{
            return await request
        }
        finally{
            this.tutorStudentsRequests.delete(userId)
        }
    }

    async getAllByTutorUserIdForAdmin(userId: number): Promise<{students: IStudent[], students_count: number}>{
        const currentRequest = this.tutorStudentsRequests.get(userId)
        if(currentRequest){
            return currentRequest
        }

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students/for_admin/by_tutor/${userId}`, {
                method: 'POST',
            })
            const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
            return {students: students.map(normalizeStudent), students_count}
        })()

        this.tutorStudentsRequests.set(userId, request)

        try{
            return await request
        }
        finally{
            this.tutorStudentsRequests.delete(userId)
        }
    }

    async getAllByTutorFilters(userId: number, work_tg_ids: number[], is_lost: boolean, is_archive?: boolean, payment_ids: number[] = [], labels: string[] = [], tag_ids: number[] = []): Promise<{students: IStudent[], students_count: number}> {
        const body: Record<string, unknown> = {work_tg_ids, is_lost, is_archive, payment_ids, labels, tag_ids}
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students/by_tutor/${userId}/filter`, {
            method: 'POST',
            body: JSON.stringify(body)
        })
        const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
        return {students: students.map(normalizeStudent), students_count}
    }

    async move(old_user_id: number, new_user_id: number, student_id?: number) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students/move`, {
            method: "POST",
            body: JSON.stringify({
                old_user_id,
                new_user_id,
                student_id
            })
        })
    }

    async getFinance(id: number, from: string, to: string): Promise<IStudentFinance> {
        if(this.controller){
            this.controller.abort()
        }
        this.controller = new AbortController()
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/finance', {
            method: "POST",
            body: JSON.stringify({
                from,
                to
            }),
            signal: this.controller.signal
        })
        const {data}: {data: IStudentFinance} = await res.json()
        this.controller = null;
        return data
    }

    async get(id: number): Promise<IStudentData>{
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id)
        const response: {student: IStudentData; sanction_sequence_number?: number | null} = await res.json()
        return normalizeStudent({
            ...response.student,
            sanction_sequence_number: response.sanction_sequence_number ?? response.student.sanction_sequence_number ?? null,
        })
    }

    async resetSanctionSequence(studentId: number): Promise<void> {
        await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/students/${studentId}/sanction-sequence/reset`, {
            method: 'POST',
            body: JSON.stringify({}),
        })
    }

    async updateSanctionSequence(studentId: number, value: number): Promise<void> {
        await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/students/${studentId}/sanction-sequence/update`, {
            method: 'POST',
            body: JSON.stringify({
                studentId,
                sanctionSequenceNumber: value,
            }),
        })
    }

    async getByPaymentUrl(url: string): Promise<IStudentByPaymentUrl> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/payment_url', {
            method: "POST",
            body: JSON.stringify({url})
        })
        const data: {student: IStudentByPaymentUrl} = await res.json()
        return data.student
    }

    async delete(id: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id, {
            method: "DELETE"
        })
    }

    async getWorkTgs(): Promise<IWorkTgOption[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/work_tgs')
        const data: {workTgs: IWorkTgOptionResponse[]} = await res.json()
        return data.workTgs.map(mapWorkTgOption)
    }

    async getAll(): Promise<{students: IStudent[], students_count: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students')
        const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
        return {students: students.map(normalizeStudent), students_count}
    }

    async getArchiveAll(): Promise<{students: IStudent[], students_count: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/archive')
        const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
        return {students: students.map(normalizeStudent), students_count}
    }

    async getAllByFilters(work_tg_ids: number[], is_lost: boolean, is_archive?: boolean, payment_ids: number[] = [], labels: string[] = [], tag_ids: number[] = []): Promise<{students: IStudent[], students_count: number}> {
        const body: Record<string, unknown> = {work_tg_ids, is_lost, is_archive, payment_ids, labels, tag_ids}
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/filter', {
            method: 'POST',
            body: JSON.stringify(body)
        })
        const {students, students_count}: {students: IStudent[], students_count: number} = await res.json()
        return {students: students.map(normalizeStudent), students_count}
    }

    async getLabels() {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/students/labels')
        const data = await res.json() as {labels?: Array<{text: string; background_color?: string; backgroundColor?: string; text_color?: string; textColor?: string}>}
        return (data.labels || []).map(label => ({
            text: label.text,
            background_color: label.background_color || label.backgroundColor || '',
            text_color: label.text_color || label.textColor || '',
        }))
    }

    async changeWallet(id: number, balance: string){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/wallet', {
            method: "POST",
            body: JSON.stringify({student_id: id, balance})
        })
    }
    
    async changeLesson(id: number, date: string, duration: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/lessons/' + id, {
            method: "POST",
            body: JSON.stringify({date, duration})
        })
    }
        
    async archive(id: number, comment: string){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/archive', {
            method: "POST",
            body: JSON.stringify({comment})
        })
    }

    async unarchive(id: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/unarchive', {
            method: "POST",
        })
    }

    async getLessons(id: number, from: string, to: string): Promise<{lessons: ILesson[], lessons_count: number, total_hours: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/lessons', {
            method: "POST",
            body: JSON.stringify({from, to})
        })
        const {lessons, lessons_count, total_hours}: {lessons: ILesson[], lessons_count: number, total_hours: number} = await res.json()
        return {lessons, lessons_count, total_hours}
    }

    async deleteLesson(id: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/lessons/' + id, {
            method: "DELETE"
        })
    }

    async notificationPush(id: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/notifications/push', {
            method: 'POST'
        })
    }

    async notificationPushAllStudents(){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/push_all_students', {
            method: 'POST'
        })
    }

    async transactions(id: number, from: string, to: string): Promise<{transactions: ITransaction[], transactions_count: number, total_confirmed_amount: string}>{
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/transactions', {
            method: "POST",
            body: JSON.stringify({from, to})
        })
        const {transactions, transactions_count, total_confirmed_amount}: {transactions: ITransaction[], transactions_count: number, total_confirmed_amount: string} = await res.json()
        return {transactions, transactions_count, total_confirmed_amount}
    }

    async addManualTransaction(studentId: number, amount: number): Promise<string> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + studentId + '/transactions/manual', {
            method: "POST",
            body: JSON.stringify({amount})
        })
        const {id}: {id: string} = await res.json()
        return id
    }

    async deleteManualTransaction(studentId: number, transactionId: string): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + studentId + '/transactions/' + transactionId + '/delete', {
            method: "POST"
        })
    }

    async changeAllPayment(payment_id: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/change_all_payment', {
            method: "POST",
            body: JSON.stringify({payment_id})
        })
    }

    async notifications(id: number, from: string, to: string): Promise<{notifications: INotification[], notifications_count: number}>{
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/notifications', {
            method: "POST",
            body: JSON.stringify({from, to})
        })
        const {notifications, notifications_count}: {notifications: INotification[], notifications_count: number} = await res.json()
        return {notifications, notifications_count}
    }

    async changePayment(studentId: number, new_payment_id: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/students/${studentId}/change_payment`, {
            method: "POST",
            body: JSON.stringify({new_payment_id})
        })
    }

    async getComments(id: number){
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + id + '/comments')
        const {comments, comments_count}: {comments: IComment[], comments_count: number} = await res.json()
        return {comments, comments_count}
    }

    async addComment(studentId: number, text: string){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + studentId + '/comments', {
            method: "POST",
            body: JSON.stringify({text})
        })
    }
    
    async deleteComment(studentId: number, commentId: number){
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/students/' + studentId + '/comments/' + commentId, {
            method: "DELETE",
        })
    }

    async getTags(): Promise<IStudentTag[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/students/tags')
        const {tags}: {tags: IStudentTagResponse[]} = await res.json()
        return tags.map(mapStudentTag)
    }

    async getStudentTags(studentId: number): Promise<IStudentTag[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/students/${studentId}/tags`)
        const {tags}: {tags: IStudentTagResponse[]} = await res.json()
        return tags.map(mapStudentTag)
    }

    async createTag(tagName: string): Promise<IStudentTag> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/students/tags', {
            method: 'POST',
            body: JSON.stringify({tagName})
        })
        const {tag}: {tag: IStudentTagResponse} = await res.json()
        return mapStudentTag(tag)
    }

    async updateTag(tagId: number, tagName: string): Promise<IStudentTag> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/students/tags/${tagId}`, {
            method: 'POST',
            body: JSON.stringify({tagId, tagName})
        })
        const {tag}: {tag: IStudentTagResponse} = await res.json()
        return mapStudentTag(tag)
    }

    async deleteTag(tagId: number, newTagId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/students/tags/${tagId}/delete`, {
            method: 'POST',
            body: JSON.stringify({newTagId})
        })
    }

    async addTagToStudent(studentId: number, tagId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/students/${studentId}/tags/add`, {
            method: 'POST',
            body: JSON.stringify({studentId, tagId})
        })
    }

    async removeTagFromStudent(studentId: number, tagId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/students/${studentId}/tags/remove`, {
            method: 'POST',
            body: JSON.stringify({studentId, tagId})
        })
    }
}

export const studentService = new StudentService()
